import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, LOGO_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <a href="/" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6" data-testid="back-to-site">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </a>
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Prowess Services" className="h-16 w-16 object-contain rounded-md mb-3" />
            <h1 className="text-2xl font-display font-bold text-slate-900">Admin Login</h1>
            <p className="text-slate-500 text-sm flex items-center gap-1.5 mt-1"><Lock className="h-3.5 w-3.5" /> Prowess Services Dashboard</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="max@goprowess.com" className="mt-1.5" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-accent hover:bg-blue-700 py-6" data-testid="login-submit">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Signing in...</> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
